import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PushNotification from "../../../assets/Push-notifications.svg";
import "./Onboarding-7.css";
import api from "../../../axiosconfig.js";
import { toast } from "react-toastify";

// Notification definitions matching Profile Notification settings
const accountNotifications = [
  {
    id: "weekly_guidance",
    label: "Weekly Reminders",
    scheduleBadge: "Every Monday",
    description:
      "Receive our weekly guidance and mindfulness feature spotlight every Monday.",
  },
  {
    id: "assessment",
    label: "Assessment Reminder",
    scheduleBadge: "1st of every month",
    description:
      "Receive a monthly reminder on the 1st of every month to take your PEARLI™ holistic well-being assessment.",
  },
  {
    id: "checkins",
    label: "Check-ins Reminder",
    scheduleBadge: "Mondays & Wednesdays",
    description:
      "Receive reminders on Mondays and Wednesdays to complete your daily focus and reflection check-ins.",
  },
  {
    id: "sherpa",
    label: "Talk to Sherpa AI",
    scheduleBadge: "Every Tuesday",
    description:
      "Receive a reminder every Tuesday to check in and converse with your 24/7 Sherpa AI companion.",
  },
  {
    id: "live_events",
    label: "Join Live Events Reminder",
    scheduleBadge: "Every Thursday",
    description:
      "Receive a weekly discovery update every Thursday highlighting upcoming live expert sessions and workshops.",
  },
];

const CACHE_KEY = "mindsherpa_notification_preferences";

export const Onboarding7 = () => {
  const navigate = useNavigate();

  // Modal open/close state
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default: all notifications enabled ("first select all")
  const [preferences, setPreferences] = useState({
    weekly_guidance: true,
    assessment: true,
    checkins: true,
    sherpa: true,
    live_events: true,
  });

  // Check if all are currently selected
  const isAllSelected = Object.values(preferences).every(Boolean);

  // Toggle individual notification
  const handleTogglePreference = (id) => {
    setPreferences((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Toggle all at once
  const handleToggleAll = () => {
    const nextVal = !isAllSelected;
    setPreferences({
      weekly_guidance: nextVal,
      assessment: nextVal,
      checkins: nextVal,
      sherpa: nextVal,
      live_events: nextVal,
    });
  };

  // When user clicks "Keep me on track" -> open popup with all selected
  const handleKeepMeOnTrack = () => {
    setPreferences({
      weekly_guidance: true,
      assessment: true,
      checkins: true,
      sherpa: true,
      live_events: true,
    });
    setShowModal(true);
  };

  // Save selected notifications from popup and continue
  const handleSavePreferences = async () => {
    setIsSubmitting(true);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(preferences));
      await api.post("/profile", {
        request: "setNotificationPreferences",
        preferences,
      });
      toast.success("Notification preferences saved!");
    } catch (err) {
      console.error("Error saving notification preferences:", err);
      // Still allow navigation even if backend call has network hiccup
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
      navigate("/Home");
    }
  };

  // When user clicks "Not Now" -> set ALL notifications as false and navigate
  const handleNotNow = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    const allFalsePrefs = {
      weekly_guidance: false,
      assessment: false,
      checkins: false,
      sherpa: false,
      live_events: false,
    };

    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(allFalsePrefs));
      await api.post("/profile", {
        request: "setNotificationPreferences",
        preferences: allFalsePrefs,
      });
      toast.info("All notifications turned off.");
    } catch (err) {
      console.error("Error disabling notification preferences:", err);
    } finally {
      setIsSubmitting(false);
      navigate("/Home");
    }
  };

  return (
    <div className="onboarding7-page-wrapper">
      {/* Top Navigation / Glassmorphic Back button */}
      <button
        type="button"
        className="onboarding7-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Go Back"
      >
        <span className="onboarding7-back-arrow" aria-hidden="true">
          &larr;
        </span>
        <span className="onboarding7-back-label">Back</span>
      </button>

      {/* Main Content Container (Centered horizontally & vertically) */}
      <div className="onboarding7-main-card">
        {/* Title & Subtitle */}
        <div className="onboarding7-heading-group">
          <h1 className="onboarding7-page-title">
            Want a daily nudge toward better well-being?
          </h1>
          <p className="onboarding7-page-subtitle">
            Users who set reminders are more likely to stay consistent and see
            real progress.
          </p>
        </div>

        {/* Push Notification Illustration */}
        <div className="onboarding7-illustration-container">
          <img
            src={PushNotification}
            alt="Push Notifications"
            className="onboarding7-illustration-img"
          />
        </div>

        <p className="onboarding7-disclaimer">
          * We’ll only send you 1 gentle reminder per day.
        </p>

        {/* Action Buttons */}
        <div className="onboarding7-actions-group">
          <button
            type="button"
            className="onboarding7-continue-btn"
            onClick={handleKeepMeOnTrack}
            disabled={isSubmitting}
          >
            Keep me on track
          </button>
          <button
            type="button"
            className="onboarding7-skip-btn"
            onClick={handleNotNow}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Not Now"}
          </button>
        </div>
      </div>

      {/* =========================================================================
          Notification Preferences Modal / Popup
          ========================================================================= */}
      {showModal && (
        <div
          className="onboarding7-modal-backdrop"
          onClick={() => setShowModal(false)}
        >
          <div
            className="onboarding7-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Modal Header */}
            <div className="onboarding7-modal-header">
              <div>
                <h2 id="modal-title" className="onboarding7-modal-title">
                  Notification Preferences
                </h2>
                <p className="onboarding7-modal-subtitle">
                  We’ll send you reminders to help you stay mindful and
                  consistent.
                </p>
              </div>
              <button
                type="button"
                className="onboarding7-modal-close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Quick Select All Toggle Bar */}
            <div className="onboarding7-modal-quick-bar">
              <span className="onboarding7-quick-label">
                {isAllSelected
                  ? "All notifications enabled"
                  : "Customize reminders"}
              </span>
              <button
                type="button"
                className="onboarding7-quick-toggle-btn"
                onClick={handleToggleAll}
              >
                {isAllSelected ? "Deselect All" : "Select All"}
              </button>
            </div>

            {/* Scrollable List of Notification Items */}
            <div className="onboarding7-modal-list">
              {accountNotifications.map(
                ({ id, label, description, scheduleBadge }) => {
                  const isEnabled = Boolean(preferences[id]);
                  return (
                    <div key={id} className="onboarding7-notif-item">
                      <div className="onboarding7-notif-info">
                        <div className="onboarding7-notif-title-row">
                          <span className="onboarding7-notif-label">
                            {label}
                          </span>
                          {scheduleBadge && (
                            <span className="onboarding7-schedule-badge">
                              {scheduleBadge}
                            </span>
                          )}
                        </div>
                        <p className="onboarding7-notif-desc">{description}</p>
                      </div>

                      {/* Toggle Switch */}
                      <label
                        className="onboarding7-switch"
                        aria-label={`Toggle ${label}`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => handleTogglePreference(id)}
                        />
                        <span className="onboarding7-slider"></span>
                      </label>
                    </div>
                  );
                }
              )}
            </div>

            {/* Modal Actions Footer */}
            <div className="onboarding7-modal-footer">
              <button
                type="button"
                className="onboarding7-modal-save-btn"
                onClick={handleSavePreferences}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save & Continue"}
              </button>
              <button
                type="button"
                className="onboarding7-modal-cancel-btn"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding7;
